/**
 * Custom Carousel Video Controller
 * Handles video autoplay/pause based on active slide in custom carousels
 */

/**
 * Play video in a slide
 * @param {HTMLElement} slide - The slide element
 */
function playVideoInSlide(slide) {
  const customCard = slide.querySelector('.custom-card[data-has-video="true"]');
  if (!customCard) return;
  
  const deferredMedia = customCard.querySelector('deferred-media');
  if (!deferredMedia) return;
  
  // Check if media is already loaded
  if (deferredMedia.getAttribute('data-media-loaded')) {
    // Media is loaded, just play it
    const video = deferredMedia.querySelector('video');
    const iframe = deferredMedia.querySelector('iframe[data-video-type]');
    
    if (video) {
      video.play().catch(() => {
        // Autoplay may be blocked, that's okay
      });
    } else if (iframe instanceof HTMLIFrameElement) {
      // For YouTube/Vimeo videos
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    }
  } else {
    // Media not loaded yet, load and play it
    if ('showDeferredMedia' in deferredMedia && typeof deferredMedia.showDeferredMedia === 'function') {
      deferredMedia.showDeferredMedia();
      
      // Wait for media to load, then play
      setTimeout(() => {
        const video = deferredMedia.querySelector('video');
        const iframe = deferredMedia.querySelector('iframe[data-video-type]');
        
        if (video) {
          video.play().catch(() => {
            // Autoplay may be blocked, that's okay
          });
        } else if (iframe instanceof HTMLIFrameElement) {
          iframe.contentWindow?.postMessage(
            iframe.dataset.videoType === 'youtube'
              ? '{"event":"command","func":"playVideo","args":""}'
              : '{"method":"play"}',
            '*'
          );
        }
      }, 100);
    }
  }
}

/**
 * Pause video in a slide
 * @param {HTMLElement} slide - The slide element
 */
function pauseVideoInSlide(slide) {
  const customCard = slide.querySelector('.custom-card[data-has-video="true"]');
  if (!customCard) return;
  
  const deferredMedia = customCard.querySelector('deferred-media');
  if (!deferredMedia) return;
  
  // Use the deferred-media's pause method
  if ('pauseMedia' in deferredMedia && typeof deferredMedia.pauseMedia === 'function') {
    deferredMedia.pauseMedia();
  } else {
    // Fallback: manually pause
    const video = deferredMedia.querySelector('video');
    const iframe = deferredMedia.querySelector('iframe[data-video-type]');
    
    if (video) {
      video.pause();
    } else if (iframe instanceof HTMLIFrameElement) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    }
  }
}

/**
 * Initialize the first slide's video to autoplay
 */
function initializeFirstSlide() {
  // Find all carousels in custom carousel sections
  const carousels = document.querySelectorAll('.ui-test-custom-carousel slideshow-component');
  
  carousels.forEach((carousel) => {
    const firstSlide = carousel.querySelector('slideshow-slide[aria-hidden="false"]');
    if (firstSlide instanceof HTMLElement) {
      playVideoInSlide(firstSlide);
    }
  });
}

/**
 * Handle slide change events
 * @param {Event} event - The slideshow:select event
 */
function handleSlideChange(event) {
  const customEvent = /** @type {CustomEvent} */ (event);
  const { slide, previousIndex } = customEvent.detail;
  
  if (!(slide instanceof HTMLElement)) return;
  
  // Only handle slides within custom carousel sections
  const customCarousel = slide.closest('.ui-test-custom-carousel');
  if (!customCarousel) return;
  
  // Get all slides in the carousel
  const allSlides = Array.from(customCarousel.querySelectorAll('slideshow-slide'));
  
  // Pause previous slide's video
  if (previousIndex !== null && previousIndex !== undefined && allSlides[previousIndex] && allSlides[previousIndex] !== slide) {
    const prevSlide = allSlides[previousIndex];
    if (prevSlide instanceof HTMLElement) {
      pauseVideoInSlide(prevSlide);
    }
  }
  
  // Play current slide's video
  playVideoInSlide(slide);
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Listen for slideshow select events
    document.addEventListener('slideshow:select', handleSlideChange);
    
    // Initialize: autoplay first slide's video if it exists
    setTimeout(initializeFirstSlide, 100);
  });
} else {
  // Listen for slideshow select events
  document.addEventListener('slideshow:select', handleSlideChange);
  
  // Initialize: autoplay first slide's video if it exists
  setTimeout(initializeFirstSlide, 100);
}
